import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, Trash2, Plus, Minus, ArrowRight, Package, 
  ChevronLeft, Store, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useCart } from '../contexts/CartContext';
import { PriceDisplay } from '../components/PriceDisplay';

export default function CartPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  
  const { 
    cartItems, 
    removeFromCart, 
    updateQuantity, 
    clearCart, 
    getCartTotal, 
    isLoading 
  } = useCart();

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`}>
        <Loader2 className="h-12 w-12 animate-spin text-[#1E9BD9]" />
      </div>
    );
  }

  const subtotal = getCartTotal();
  const deliveryFee = subtotal > 0 ? 0 : 0; // Free delivery for now
  const total = subtotal + deliveryFee;

  // Group items by store
  const itemsByStore = cartItems.reduce((acc, item) => {
    const storeId = item.store_id || 'unknown';
    if (!acc[storeId]) {
      acc[storeId] = {
        store_name: item.store_name || 'متجر',
        items: []
      };
    }
    acc[storeId].items.push(item);
    return acc;
  }, {});

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={`sticky top-0 z-40 ${isDarkMode ? 'bg-[#0a0a0a]/90' : 'bg-white/90'} backdrop-blur-md border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
          >
            <ChevronLeft className={`h-6 w-6 ${isAr ? 'rotate-180' : ''} ${isDarkMode ? 'text-white' : 'text-gray-800'}`} />
          </button>
          <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {isAr ? 'سلة التسوق' : 'Shopping Cart'}
          </h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {cartItems.length === 0 ? (
          /* Empty Cart */
          <div className={`rounded-2xl p-12 text-center ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
            <ShoppingCart className={`h-24 w-24 mx-auto mb-6 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <h2 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {isAr ? 'السلة فارغة' : 'Your cart is empty'}
            </h2>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {isAr ? 'ابدأ التسوق وأضف منتجات للسلة' : 'Start shopping and add products to your cart'}
            </p>
            <Link to="/products">
              <Button className="bg-[#1E9BD9] hover:bg-[#1789C2]">
                <Package className="h-5 w-5 me-2" />
                {isAr ? 'تصفح المنتجات' : 'Browse Products'}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {/* Clear Cart Button */}
              <div className="flex justify-between items-center">
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {isAr ? `${cartItems.length} منتج` : `${cartItems.length} items`}
                </h2>
                <button
                  onClick={clearCart}
                  className={`text-sm flex items-center gap-1 ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-600'}`}
                >
                  <Trash2 className="h-4 w-4" />
                  {isAr ? 'إفراغ السلة' : 'Clear Cart'}
                </button>
              </div>

              {/* Items grouped by store */}
              {Object.entries(itemsByStore).map(([storeId, storeData]) => (
                <div 
                  key={storeId}
                  className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}
                >
                  {/* Store Header */}
                  <div className={`px-4 py-3 flex items-center gap-2 ${isDarkMode ? 'bg-white/5 border-b border-white/10' : 'bg-gray-50 border-b'}`}>
                    <Store className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {storeData.store_name}
                    </span>
                  </div>

                  {/* Store Items */}
                  <div className="divide-y divide-gray-100 dark:divide-white/10">
                    {storeData.items.map((item) => (
                      <div key={item.id} className="p-4 flex gap-4">
                        {/* Product Image */}
                        <div className="w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className={`w-full h-full flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                              <Package className={`h-8 w-8 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-medium mb-1 line-clamp-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {isAr ? item.name : item.nameEn}
                          </h3>
                          
                          {/* Price */}
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-[#1E9BD9] font-bold">
                              <PriceDisplay price={item.price} />
                            </span>
                            {item.originalPrice && item.originalPrice > item.price && (
                              <span className={`line-through text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                <PriceDisplay price={item.originalPrice} />
                              </span>
                            )}
                          </div>

                          {/* Quantity Controls */}
                          <div className="flex items-center justify-between">
                            <div className={`flex items-center gap-2 rounded-lg ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                              >
                                <Minus className={`h-4 w-4 ${isDarkMode ? 'text-white' : 'text-gray-600'}`} />
                              </button>
                              <span className={`w-8 text-center font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                disabled={item.stock && item.quantity >= item.stock}
                                className={`p-2 rounded-lg transition-colors ${
                                  item.stock && item.quantity >= item.stock
                                    ? 'opacity-50 cursor-not-allowed'
                                    : isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-200'
                                }`}
                              >
                                <Plus className={`h-4 w-4 ${isDarkMode ? 'text-white' : 'text-gray-600'}`} />
                              </button>
                            </div>

                            <button
                              onClick={() => removeFromCart(item.id)}
                              className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className={`rounded-2xl p-6 sticky top-20 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
                <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {isAr ? 'ملخص الطلب' : 'Order Summary'}
                </h3>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      {isAr ? 'المجموع الفرعي' : 'Subtotal'}
                    </span>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-800'}>
                      <PriceDisplay price={subtotal} />
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      {isAr ? 'التوصيل' : 'Delivery'}
                    </span>
                    <span className="text-green-500">
                      {deliveryFee === 0 ? (isAr ? 'مجاني' : 'Free') : <PriceDisplay price={deliveryFee} />}
                    </span>
                  </div>
                  <div className={`pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="flex justify-between">
                      <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {isAr ? 'الإجمالي' : 'Total'}
                      </span>
                      <span className="text-xl font-bold text-[#1E9BD9]">
                        <PriceDisplay price={total} />
                      </span>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => navigate('/checkout')}
                  className="w-full py-6 text-lg font-semibold rounded-xl bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:opacity-90"
                >
                  {isAr ? 'متابعة للدفع' : 'Proceed to Checkout'}
                  <ArrowRight className={`h-5 w-5 ms-2 ${isAr ? 'rotate-180' : ''}`} />
                </Button>

                <Link 
                  to="/products"
                  className={`block text-center mt-4 text-sm ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {isAr ? 'متابعة التسوق' : 'Continue Shopping'}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
